<script setup lang="ts">
import {ref, useTemplateRef, watch} from "vue";
import {saving} from "@/Composables/api";
import {Button, Textarea, Icon} from "@/Components/Library";
import {onClickOutside, onLongPress} from "@vueuse/core";
import {useMainStore} from "@/Stores/store";
import {useToast} from "primevue/usetoast";
import useAudioRecorder from "@/Composables/audioRecorder";

const $store = useMainStore();
const toast = useToast();

const {placeholder = "Write a comment", allowVoice = false} = defineProps<{
    placeholder?: string;
    disabled?: boolean;
    saving?: string;
    allowVoice?: boolean;
}>();
const emit = defineEmits<{(e: "submit"): void; (e: "cancel"): void}>();

const model = defineModel<{text: string; audioFile: Blob | null}>({default: {text: "", audioFile: null}});
const commentTextarea = useTemplateRef<Textarea | null>("commentTextarea");

const {
    audioFile,
    recordingState,
    playing: isPlaying,
    startRecording,
    stopRecording,
    playRecording,
    pausePlayback,
    deleteRecording,
} = useAudioRecorder({
    enablePauseResume: false,
    enableProgressTracking: false,
    fileNamingStrategy: "timestamp",
    onRecordingError: (error) => {
        console.error("Failed to start recording:", error);
        toast.add({
            severity: "error",
            summary: "Recording failed",
            detail: "Could not access microphone",
            life: 3000,
        });
    },
});

const resetModel = () => (model.value = {text: "", audioFile: null});

const reset = () => {
    resetModel();
    deleteRecording();
};

const togglePlayback = () => {
    if (isPlaying.value) {
        pausePlayback();
    } else {
        playRecording();
    }
};

const micButton = useTemplateRef("micButton");
onLongPress(micButton, startRecording, {
    delay: 500,
    onMouseUp: (duration, distance, isLongPress) => {
        if (!isLongPress && (recordingState.value === "idle" || recordingState.value === "recorded")) {
            toast.add({
                severity: "warn",
                summary: "Voice recording",
                detail: "Long press to start recording",
                life: 3000,
            });
        }
    },
});

const cancel = () => {
    reset();
    emit("cancel");
};

const submitComment = () => {
    console.log(recordingState.value);
    let text = model.value?.text.trim();
    if ((recordingState.value === "recorded" && audioFile.value) || text) {
        emit("submit", {
            type: "text",
            data: text,
        });
        deleteRecording();
    } else {
        toast.add({
            severity: "warn",
            summary: "Comment",
            detail: "Comment is empty",
            life: 3000,
        });
    }
};

defineExpose({
    commentTextarea,
    reset,
});
const showMentionMenu = ref(false);
const mentionMenu = useTemplateRef("mentionMenu");
onClickOutside(mentionMenu, (event: any) => (showMentionMenu.value = false));

const showMention = (e: any) => {
    if (e.key === "@") {
        let selection = document.getSelection();
        let prevChar = model.value?.substring(selection.focusOffset - 1, selection.focusOffset);
        console.log(selection.focusOffset, prevChar);
        if (!prevChar || prevChar === " ") {
            showMentionMenu.value = true;
        }
    }
};
const mention = (member: any) => {
    let str = model.value.substring(0, model.value.length - 1);
    console.log(commentTextarea.value);
    str += `@[${member.first_name}${member.last_name}] `;
    model.value = str;
    commentTextarea.value.$el.focus();
    showMentionMenu.value = false;

    //This is only relevant to contenteditable. Keeping this in case we decide to switch to a more complex mention solution
    // let selection = document.getSelection();
    // console.log(selection);
    // commentTextarea.value.innerHTML = `${str}<span class="bg-black" data-id="${member.id}">@[${member.first_name}${member.last_name}]</span> `;
    // let sel = window.getSelection();
    // sel.selectAllChildren(commentTextarea.value);
    // sel.collapseToEnd();
};

//This is only relevant to contenteditable. Keeping this in case we decide to switch to a more complex mention solution
// const focussed = ref(false);
// const setText = ($event) => {
//     let text = commentTextarea.value.innerHTML;
//     console.log(text);
//     text = commentTextarea.value.innerText.replace(/<((?!span\s).)*?>/gi, "");
//     model.value = text;
//     console.log(model.value);
// };

watch(audioFile, (newFile) => {
    if (newFile) {
        model.value.audioFile = newFile;
    }
});
</script>
<template>
    <form @submit.prevent="submitComment" class="flex w-full flex-col gap-2">
        <div
            class="group relative w-full cursor-text rounded-md border border-stone-300 bg-stone-50 outline-1 transition-colors duration-200 hover:border-black disabled:opacity-60 has-[:focus]:border-black has-[:focus]:ring has-[:focus]:ring-askr-green-500/50 dark:border-stone-600 dark:bg-stone-900 dark:hover:border-stone-500"
            @click="commentTextarea?.$el.focus"
        >
            <!-- Textarea (hidden when recording/recorded) -->
            <Textarea
                v-if="recordingState === 'idle'"
                ref="commentTextarea"
                v-model="model.text"
                class="w-full border-transparent shadow-none outline-0 hover:border-transparent focus:ring-transparent dark:border-transparent dark:focus:ring-transparent"
                id="comment"
                autoResize
                rows="1"
                maxlength="1000"
                :placeholder
                :disabled="!!saving || !!disabled"
                :readonly="!!disabled"
                @keydown="showMention"
            />

            <!-- Recording/Recorded State UI -->
            <div v-else class="flex items-center gap-2 p-3">
                <Icon
                    v-if="recordingState === 'recording'"
                    icon="mdi:microphone"
                    width="24"
                    class="animate-pulse text-red-600"
                />
                <Icon v-else icon="mdi:check-circle" width="24" class="text-green-600" />

                <span class="text-sm text-stone-600 dark:text-stone-400">
                    {{ recordingState === "recording" ? "Recording..." : "Recording complete" }}
                </span>
            </div>

            <!-- Bottom action bar -->
            <div class="flex items-center justify-end p-2" v-auto-animate>
                <Button @click.stop="cancel" class="mr-1 !px-1 !py-0 text-xs font-normal" text>Cancel</Button>
                <Button
                    v-tooltip.left="
                        recordingState === 'recorded'
                            ? 'Press and hold to re-record'
                            : 'Press and hold to record voice message'
                    "
                    v-if="allowVoice && (recordingState === 'idle' || recordingState === 'recorded')"
                    ref="micButton"
                    type="button"
                    :icon="{icon: 'mdi:microphone', width: 24}"
                    class="ml-auto mr-2 h-8 w-8 hover:bg-stone-200 dark:hover:bg-stone-700"
                    text
                    rounded
                />

                <!-- Stop button (recording state) -->
                <Button
                    v-else-if="recordingState === 'recording'"
                    type="button"
                    @click="stopRecording"
                    :icon="{icon: 'mdi:stop', width: 24}"
                    class="ml-auto mr-2 h-8 w-8 bg-red-600 text-white hover:bg-red-700"
                    rounded
                />
                <template v-if="recordingState === 'recorded'">
                    <Button
                        type="button"
                        @click="togglePlayback"
                        :icon="{icon: isPlaying ? 'mdi:pause' : 'mdi:play', width: 24}"
                        class="mr-2 h-8 w-8 bg-askr-green text-white hover:brightness-110"
                        rounded
                        v-tooltip.top="isPlaying ? 'Pause playback' : 'Play recording'"
                    />
                    <Button
                        type="button"
                        @click="deleteRecording"
                        :icon="{icon: 'mdi:trash-outline', width: 24}"
                        class="mr-2 h-8 w-8 bg-red-600 text-white hover:bg-red-700"
                        rounded
                        v-tooltip.top="'Delete recording'"
                    />
                </template>
                <Button
                    type="submit"
                    class="h-8 w-8 bg-askr-green-500 text-white hover:brightness-110"
                    icon="mdi:send"
                    :loading="!!saving"
                    rounded
                    :disabled="disabled"
                />
            </div>

            <!-- Mention menu -->
            <div class="absolute bottom-0 left-0 z-50 w-full translate-y-full" v-auto-animate>
                <div
                    v-if="showMentionMenu"
                    ref="mentionMenu"
                    class="rounded-lg bg-white p-1 shadow-lg shadow-black dark:bg-stone-800"
                >
                    <ul>
                        <li v-for="member in $store.question.group.members" :key="member.id">
                            <button
                                type="button"
                                class="w-full rounded px-2 py-1 text-left transition hover:bg-stone-900"
                                @click="mention(member)"
                            >
                                {{ member.first_name }} {{ member.last_name }}
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </form>
</template>
<style scoped></style>
